import {
	Component,
	computed,
	effect,
	inject,
	input,
	output,
	signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import {
	type QuoteFilterOption,
	type QuoteFilters
} from '../../../models/quote-filters';
import { type Quote } from '../../../models/quote.model';
import { ViewportService } from '../../../services/viewport.service';
import { UNASSIGNED_INSIDE_REP } from './quote-filters.helpers';

@Component({
	selector: 'app-quote-filters',
	standalone: true,
	imports: [
		FormsModule,
		ButtonModule,
		DatePickerModule,
		IconFieldModule,
		InputIconModule,
		InputTextModule,
		SelectModule,
		TooltipModule
	],
	templateUrl: './quote-filters.component.html',
	styleUrl: './quote-filters.component.sass'
})
export class QuoteFiltersComponent {
	// The full, unfiltered quote set — dropdown options are derived from it so
	// they stay stable instead of shrinking as the user narrows the list.
	public readonly quotes = input<Quote[]>([]);
	public readonly isSyncing = input(false);

	public readonly filtersApplied = output<QuoteFilters>();
	public readonly refreshQuotes = output();
	public readonly downloadRequested = output();

	public readonly viewport = inject(ViewportService);
	public readonly isExpanded = signal(!this.viewport.isMobile());

	public searchValue = '';
	public nsCustomerValue = '';
	public nsStatusValue = '';
	public quoteStatusValue = '';
	public insideRepValue = '';
	public startDateValue: Date | null = null;
	public endDateValue: Date | null = null;
	public dueStartDateValue: Date | null = null;
	public dueEndDateValue: Date | null = null;

	public readonly nsCustomerOptions = computed<QuoteFilterOption[]>(() =>
		this.toOptions(this.quotes().map((q) => q.nsCustomerName))
	);

	public readonly nsStatusOptions = computed<QuoteFilterOption[]>(() =>
		this.toOptions(this.quotes().map((q) => q.nsEstimateStatus))
	);

	public readonly quoteStatusOptions = computed<QuoteFilterOption[]>(() =>
		this.toOptions(this.quotes().map((q) => q.nsQuoteStatus))
	);

	public readonly insideRepOptions = computed<QuoteFilterOption[]>(() => {
		const options = this.toOptions(this.quotes().map((q) => q.insideRep));

		if (this.quotes().some((q) => !q.insideRep)) {
			options.unshift({
				label: 'Unassigned',
				value: UNASSIGNED_INSIDE_REP
			});
		}

		return options;
	});

	public applyFilters(): void {
		this.filtersApplied.emit({
			search: this.searchValue,
			nsCustomer: this.nsCustomerValue,
			insideRep: this.insideRepValue,
			nsStatus: this.nsStatusValue,
			quoteStatus: this.quoteStatusValue,
			startDate: this.toStartOfDayIso(this.startDateValue),
			endDate: this.toEndOfDayIso(this.endDateValue),
			dueStartDate: this.toStartOfDayIso(this.dueStartDateValue),
			dueEndDate: this.toEndOfDayIso(this.dueEndDateValue)
		});
	}

	public clearFilters(): void {
		this.searchValue = '';
		this.nsCustomerValue = '';
		this.nsStatusValue = '';
		this.quoteStatusValue = '';
		this.insideRepValue = '';
		this.startDateValue = null;
		this.endDateValue = null;
		this.dueStartDateValue = null;
		this.dueEndDateValue = null;
		this.applyFilters();
	}

	public refresh(): void {
		this.refreshQuotes.emit();
	}

	public requestDownload(): void {
		this.downloadRequested.emit();
	}

	public toggleExpanded(): void {
		this.isExpanded.update((expanded) => !expanded);
	}

	public constructor() {
		// Filters are always visible on desktop — only mobile gets the
		// collapsible group behavior, so force-expand whenever we cross
		// back above the breakpoint.
		effect(() => {
			if (!this.viewport.isMobile()) {
				this.isExpanded.set(true);
			}
		});
	}

	private toStartOfDayIso(value: Date | null): string {
		return value ? value.toISOString() : '';
	}

	// The picker hands back midnight, which would exclude everything that landed
	// later that same day, so "before" bounds run to the end of the day.
	private toEndOfDayIso(value: Date | null): string {
		if (!value) return '';
		const endOfDay = new Date(value);
		endOfDay.setHours(23, 59, 59, 999);
		return endOfDay.toISOString();
	}

	private toOptions(values: (string | null)[]): QuoteFilterOption[] {
		return [
			...new Set(
				values.filter((v): v is string => !!v && v.trim() !== '')
			)
		]
			.sort()
			.map((v) => ({ label: v, value: v }));
	}
}
